import type { KeyEventLike } from '../input/shortcuts';
import { matchesShortcut } from '../input/shortcuts';
import type { Command, CommandResult, MenuId } from './types';
import { shortcutsOf } from './types';

/**
 * Registry of commands plus lookup helpers used by the menu bar, the shortcut
 * handler and the (future) command palette.
 */
export class CommandRegistry<Ctx> {
  private commands = new Map<string, Command<Ctx>>();

  /** Register one command. Returns an unregister function. */
  register(command: Command<Ctx>): () => void {
    if (this.commands.has(command.id)) {
      throw new Error(`Duplicate command id: ${command.id}`);
    }
    this.commands.set(command.id, command);
    return () => this.unregister(command.id);
  }

  registerAll(commands: readonly Command<Ctx>[]): () => void {
    const undo = commands.map((c) => this.register(c));
    return () => {
      for (const unregister of undo) unregister();
    };
  }

  unregister(id: string): void {
    this.commands.delete(id);
  }

  clear(): void {
    this.commands.clear();
  }

  get(id: string): Command<Ctx> | undefined {
    return this.commands.get(id);
  }

  has(id: string): boolean {
    return this.commands.has(id);
  }

  all(): Command<Ctx>[] {
    return [...this.commands.values()];
  }

  /** Commands belonging to a menu, grouped by section then ordered. */
  forMenu(menu: MenuId, ctx: Ctx): Command<Ctx>[] {
    return this.all()
      .filter((c) => c.menu === menu)
      .filter((c) => (c.isVisible ? c.isVisible(ctx) : true))
      .sort(
        (a, b) =>
          (a.section ?? 0) - (b.section ?? 0) ||
          (a.order ?? 0) - (b.order ?? 0) ||
          a.title.localeCompare(b.title),
      );
  }

  /** Section boundaries for rendering separators: items grouped by `section`. */
  groupBySection(commands: Command<Ctx>[]): Map<number, Command<Ctx>[]> {
    const groups = new Map<number, Command<Ctx>[]>();
    for (const command of commands) {
      const key = command.section ?? 0;
      const bucket = groups.get(key);
      if (bucket) bucket.push(command);
      else groups.set(key, [command]);
    }
    return new Map([...groups.entries()].sort((a, b) => a[0] - b[0]));
  }

  isEnabled(command: Command<Ctx>, ctx: Ctx): boolean {
    if (command.plannedFor !== undefined) return false;
    return command.isEnabled ? command.isEnabled(ctx) : true;
  }

  isChecked(command: Command<Ctx>, ctx: Ctx): boolean {
    return command.isChecked ? command.isChecked(ctx) : false;
  }

  /** Why a command cannot run right now, or null when it can. */
  disabledReason(command: Command<Ctx>, ctx: Ctx): string | null {
    if (command.plannedFor !== undefined) {
      return `Planned for Iteration ${command.plannedFor}`;
    }
    if (command.isEnabled && !command.isEnabled(ctx)) {
      return command.description ?? `${command.title} is not available right now`;
    }
    return null;
  }

  /** Single entry point for running anything: menus, buttons, shortcuts, palette. */
  async execute(id: string, ctx: Ctx): Promise<CommandResult> {
    const command = this.commands.get(id);
    if (!command) return { ok: false, reason: `Unknown command: ${id}` };
    const reason = this.disabledReason(command, ctx);
    if (reason) return { ok: false, reason };
    try {
      await command.run(ctx);
      return { ok: true };
    } catch (error) {
      return {
        ok: false,
        reason: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Find and run the command bound to a key event. Returns true when a command
   * claimed the event (so the caller can preventDefault).
   */
  async runShortcut(event: KeyEventLike, ctx: Ctx): Promise<boolean> {
    for (const command of this.commands.values()) {
      if (shortcutsOf(command).length === 0) continue;
      if (!this.isEnabled(command, ctx)) continue;
      if (shortcutsOf(command).some((spec) => matchesShortcut(spec, event))) {
        const result = await this.execute(command.id, ctx);
        if (!result.ok) this.onFailure?.(result.reason);
        return true;
      }
    }
    return false;
  }

  /** Hook for the UI to surface failures from shortcut-triggered commands. */
  onFailure?: (reason: string) => void;
}
